import { msg } from '@lingui/core/macro';
import { showToast } from 'state/toast';
import { removeUndefinedValues } from 'utils/objects';
import { State } from 'utils/state';
import { IToastProps } from 'components/Toast';
import { getStorageItem, setStorageItem } from './storage';

const deviceInUseToast: IToastProps = {
  title: msg`Camera or microphone in use`,
  message: msg`Your camera or microphone is currently being used by another application. Please close other apps like Zoom, Teams, or Skype and try again.`,
  type: 'warn',
  notificationId: 'WARN_MEDIA_DEVICE_IN_USE',
};

const mediaDevicesToastWarnings: Record<string, IToastProps> = {
  NotAllowedError: {
    title: msg`VOIP not available`,
    message: msg`You have denied access to your camera and microphone.`,
    type: 'warn',
    notificationId: 'WARN_MEDIA_NOT_ALLOWED',
  },
  NotSupportedError: {
    title: msg`VOIP not available`,
    message: msg`Your browser does not support camera and microphone recording.`,
    type: 'warn',
    notificationId: 'WARN_MEDIA_NOT_SUPPORTED',
  },
  NotReadableError: deviceInUseToast,
  TrackStartError: deviceInUseToast, // Variant of NotReadableError on Chrome
  NotFoundError: {
    title: msg`No camera or microphone found`,
    message: msg`We couldn't detect a camera or microphone on your device. Please connect one and try again.`,
    type: 'warn',
    notificationId: 'WARN_MEDIA_NOT_FOUND',
  },
  OverconstrainedError: {
    title: msg`Device doesn't meet requirements`,
    message: msg`Your camera or microphone doesn't support the required settings. Try using a different device.`,
    type: 'warn',
    notificationId: 'WARN_MEDIA_OVERCONSTRAINED',
  },
  default: {
    title: msg`VOIP not available`,
    message: msg`Failed to initialize media devices.`,
    type: 'warn',
    notificationId: 'WARN_MEDIA_INIT_ERROR',
  },
};

export type VoipMediaState = {
  microphoneEnabled: boolean;
  cameraEnabled: boolean;
};

export type Stream = MediaStream | string | null;

export interface MediaDeviceConfig {
  video?: boolean;
  audio?: boolean;
  preferredCameraId?: string;
  preferredAudioId?: string;
}

export interface StreamState extends MediaDeviceConfig {
  streamId: string | null;
  isVideoEnabled: boolean;
  isAudioEnabled: boolean;
  isVideoAllowed: boolean;
  isAudioAllowed: boolean;
  isStreaming: boolean;
  initialized: boolean;
  mediaAllowed: boolean;
  error?: string;
  /** Device ids of the live tracks — what is actually in use, unlike the preferred ids. */
  activeCameraId?: string;
  activeAudioId?: string;
  videoDevices?: DeviceInfo[];
  audioDevices?: DeviceInfo[];
}

// Device choices are the only preferences; enabled flags and stream state are per-call.
function pickDeviceConfig({ video, audio, preferredCameraId, preferredAudioId }: MediaDeviceConfig): MediaDeviceConfig {
  return removeUndefinedValues({ video, audio, preferredCameraId, preferredAudioId });
}

interface DeviceInfo {
  id: string;
  label: string;
}

function isDeviceGone(error: unknown): boolean {
  return ['OverconstrainedError', 'NotFoundError'].includes((error as Error)?.name);
}

function usesDeviceId(constraints: MediaStreamConstraints): boolean {
  return hasDeviceIdConstraint(constraints.video) || hasDeviceIdConstraint(constraints.audio);
}

function singleInputConstraints(
  kind: 'audio' | 'video',
  constraint: NonNullable<MediaStreamConstraints['video']>
): MediaStreamConstraints {
  return kind === 'video' ? { video: constraint, audio: false } : { video: false, audio: constraint };
}

function hasDeviceIdConstraint(constraint: MediaStreamConstraints['video']): boolean {
  return typeof constraint === 'object' && constraint !== null && constraint.deviceId !== undefined;
}

function destroyedAcquisitionError(): Error {
  const error = new Error('MediaDeviceManager was destroyed during device acquisition');
  error.name = 'AbortError';
  return error;
}

export function formatDeviceLabel(label: string, fallbackLabel: string): string {
  const trimmedLabel = label.trimEnd();
  if (!trimmedLabel.endsWith(')')) {
    return label.trim() ? label : fallbackLabel;
  }

  let depth = 0;
  for (let index = trimmedLabel.length - 1; index >= 0; index -= 1) {
    if (trimmedLabel[index] === ')') {
      depth += 1;
    } else if (trimmedLabel[index] === '(') {
      depth -= 1;
      if (depth === 0) {
        const prefix = trimmedLabel.slice(0, index);
        if (index === 0 || /\s$/.test(prefix)) {
          return prefix.trimEnd() || fallbackLabel;
        }
        break;
      }
    }
  }

  return label.trim() ? label : fallbackLabel;
}

/**
 * This class connects to the user's media devices (camera and microphone) and provides methods to
 * toggle them on and off. It also saves the user's preferences for the media devices.
 *
 * @example
 * ```typescript
 * const mediaManager = new MediaDeviceManager();
 *
 * try {
 *   const stream = await mediaManager.init({ // Initialize with both video and audio
 *     video: true,
 *     audio: true,
 *   });
 *
 *   const videoElement = document.querySelector<HTMLVideoElement>('#videoElement');
 *   if (videoElement) {
 *     videoElement.srcObject = stream; // Attach stream to video element
 *     videoElement.play();
 *   }
 * } catch (error) {
 *   console.error('Failed to initialize media devices:', error);
 * }
 * ```
 */
export class MediaDeviceManager extends State<StreamState> {
  public stream: MediaStream | null = null;
  private destroyed = false;
  // Partial multi-input grabs aren't assigned to `stream` until every permission prompt completes.
  private readonly pendingAcquisitionTracks = new Set<MediaStreamTrack>();
  private readonly storageKey: string = 'mediaDevicePreferences';

  private readonly handleDeviceChange = () => {
    void this.refreshAvailableDevices();
  };

  public permissions: {
    video: boolean;
    audio: boolean;
  } | null = null;

  // initialization is async. This flag helps us prevent multiple initializations in parallel
  private deviceThenable: false | Promise<MediaStream> = false;

  // Counts turn-offs per kind, so a re-acquisition can tell that one landed while it waited.
  private turnOffs = { audio: 0, video: 0 };

  // The user's own mutes, set only by the toggles. Only these survive a re-grab: a kind that is off
  // because it failed (a busy camera, a failed re-acquisition) comes back on with a live track.
  private userMuted = { audio: false, video: false };

  // One re-acquisition per kind at a time. Callers that arrive while it waits share it, so a
  // single continuation decides whether the new track is kept (and published once) or stopped.
  private reacquisitions: Partial<Record<'audio' | 'video', Promise<void>>> = {};

  // Tail of the getUserMedia queue. Requests never overlap, as when every request shared one
  // pending promise, so no browser gets a second request while a permission prompt is open.
  private acquisitions: Promise<unknown> = Promise.resolve();

  constructor() {
    super();
    this.checkBrowserSupport();
    navigator.mediaDevices?.addEventListener?.('devicechange', this.handleDeviceChange);

    this.on('change', (changedKeys: (keyof StreamState)[]) => {
      const hasPreferenceChange = changedKeys.some(
        (key) => key === 'video' || key === 'audio' || key === 'preferredCameraId' || key === 'preferredAudioId'
      );
      if (hasPreferenceChange) {
        this.savePreferences(this.get() as StreamState);
      }
    });
  }

  private checkBrowserSupport(): void {
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      this.setValues({ mediaAllowed: false });
      showToast(mediaDevicesToastWarnings.NotSupportedError);
    } else {
      this.setValues({ mediaAllowed: true });
    }
  }

  public async init(userConfig?: Partial<MediaDeviceConfig>): Promise<MediaStream | undefined> {
    if (!this.get('mediaAllowed')) {
      return;
    }

    const [notAllowedToProceed] = this.emit('before-initialized', this);
    if (notAllowedToProceed === true) {
      return;
    }

    if (this.stream) {
      return this.stream;
    }

    this.checkPermissions();
    const preferences = await this.getPreferences();

    // Override preferences based on browser permissions
    const config: MediaDeviceConfig = {
      ...preferences,
      ...userConfig,
      video: userConfig?.video !== undefined ? userConfig.video : preferences.video,
      audio: userConfig?.audio !== undefined ? userConfig.audio : preferences.audio,
    };

    this.setValues(config);

    this.emit('initialized', this);

    return this.startStream();
  }

  public async getAvailableDevices(): Promise<{
    videoDevices: DeviceInfo[];
    audioDevices: DeviceInfo[];
  }> {
    const devices = await navigator.mediaDevices.enumerateDevices();

    const availableDevices = {
      videoDevices: devices
        .filter((device) => device.kind === 'videoinput')
        .map((device) => ({
          id: device.deviceId,
          label: device.label,
        })),
      audioDevices: devices
        .filter((device) => device.kind === 'audioinput')
        .map((device) => ({
          id: device.deviceId,
          label: device.label,
        })),
    };

    if (!this.destroyed) {
      this.setValues(availableDevices);
    }

    return availableDevices;
  }

  private async refreshAvailableDevices(): Promise<void> {
    try {
      await this.getAvailableDevices();
    } catch (error) {
      console.error('Failed to enumerate media devices:', error);
    }
  }

  public toggleVideo(): boolean {
    if (!this.stream) return false;
    const newValue = !this.get('isVideoEnabled');
    this.userMuted.video = !newValue;
    this.enableTrack('video', newValue);
    this.setValues({ isVideoEnabled: newValue });
    return newValue;
  }

  public toggleAudio(): boolean {
    if (!this.stream) return false;
    const newValue = !this.get('isAudioEnabled');
    this.userMuted.audio = !newValue;
    this.enableTrack('audio', newValue);
    this.setValues({ isAudioEnabled: newValue });
    return newValue;
  }

  /**
   * Records a recovered camera as ON. The live video track is enabled together with the flag, so
   * peers told "camera on" never get a track that was left disabled by a camera-off re-grab.
   */
  public markVideoRecovered(): void {
    this.userMuted.video = false;
    this.stream?.getVideoTracks().forEach((track) => {
      if (track.readyState === 'live') track.enabled = true;
    });
    this.setValues({ isVideoEnabled: true });
  }

  public enableTrack(type: 'audio' | 'video', enable: boolean): void {
    const tracks = type === 'audio' ? this.stream?.getAudioTracks() : this.stream?.getVideoTracks();
    if (!enable) {
      this.turnOffs[type] += 1;
    }

    // A rollback belongs to the stream it started on: a newer grab has set its own flags.
    const streamAtStart = this.stream;
    const rollBack = () => {
      if (this.stream !== streamAtStart || this.hasLiveTrack(type)) return;
      this.setValues(type === 'video' ? { isVideoEnabled: false } : { isAudioEnabled: false });
    };

    // Zero tracks of this kind: re-enabling must ACQUIRE one, not silently no-op over an
    // audio-only stream — `reinitializeTrack` emits `track:added`, which reaches the peers.
    if (enable && this.stream && !tracks?.length) {
      this.reinitializeTrack(type).catch((error) => {
        console.error(`Failed to acquire a new ${type} track: ${error}`);
        // The caller already flipped the enabled flag; roll it back so the control does not
        // report "on" over a stream with no track.
        rollBack();
      });
      return;
    }

    tracks?.forEach((track) => {
      track.enabled = enable;
      if (!enable) {
        track.stop();
        this.emit('track:stopped', track);
      }
    });

    // Only an ended track needs a new one; a live one (left disabled by a re-grab) just flips
    // `enabled`. Fire-and-forget by design (the caller is sync); log instead of an unhandled
    // rejection, and roll the flag back when no live track of the kind is left.
    if (enable && tracks?.length && !this.hasLiveTrack(type)) {
      this.reinitializeTrack(type).catch((error) => {
        console.error(`Failed to refresh the ${type} track: ${error}`);
        rollBack();
      });
    }
  }

  private hasLiveTrack(type: 'audio' | 'video'): boolean {
    const tracks = type === 'audio' ? this.stream?.getAudioTracks() : this.stream?.getVideoTracks();
    return Boolean(tracks?.some((track) => track.readyState === 'live'));
  }

  reinitializeTrack(type: 'audio' | 'video'): Promise<void> {
    const pending = this.reacquisitions[type];
    if (pending) return pending;

    const reacquisition = this.reacquireTrack(type).finally(() => {
      if (this.reacquisitions[type] === reacquisition) {
        delete this.reacquisitions[type];
      }
    });
    this.reacquisitions[type] = reacquisition;
    return reacquisition;
  }

  private async reacquireTrack(type: 'audio' | 'video') {
    const streamAtStart = this.stream;
    const turnOffsAtStart = this.turnOffs[type];
    // Not through initDevice: its dedupe hands a waiting caller the result of a request made for
    // other constraints (the other kind, or a new stream), and those tracks are not this
    // caller's to keep or stop.
    const newStream = await this.acquirePreferred(type);

    // getUserMedia can wait on a permission prompt. Drop the new track when, meanwhile, the
    // stream was released (nothing would own it) or the track was turned off and not back on
    // (`track:added` would publish it to every peer while the control shows OFF). The flag
    // alone is not enough: retryVoipMedia calls this with the flag off and sets it on after.
    const isEnabled = this.get(type === 'audio' ? 'isAudioEnabled' : 'isVideoEnabled');
    const turnedOff = this.turnOffs[type] !== turnOffsAtStart && !isEnabled;
    if (!streamAtStart || this.stream !== streamAtStart || turnedOff) {
      newStream.getTracks().forEach((track) => track.stop());
      return;
    }

    const newTracks = type === 'audio' ? newStream.getAudioTracks() : newStream.getVideoTracks();
    const newTrack = newTracks[0];
    // No track of the kind is a failure: `track:added` with nothing would crash the publish.
    if (!newTrack) {
      newStream.getTracks().forEach((track) => track.stop());
      throw new Error(`getUserMedia returned no ${type} track`);
    }
    const existingTracks = type === 'audio' ? this.stream?.getAudioTracks() : this.stream?.getVideoTracks();
    this.stream?.addTrack(newTrack);
    existingTracks?.forEach((track) => {
      if (track.readyState === 'ended') {
        this.stream?.removeTrack(track);
        this.emit('track:removed', track);
      }
    });
    this.recordActiveDevices();
    this.emit('track:added', newTrack, type);
  }

  /** Requests one kind on the chosen device; any device of that kind if the chosen one is gone. */
  private async acquirePreferred(type: 'audio' | 'video'): Promise<MediaStream> {
    const preferredId = this.get(type === 'video' ? 'preferredCameraId' : 'preferredAudioId') as string | undefined;
    const constraint = preferredId ? { deviceId: { exact: preferredId } } : true;
    return this.acquireWithFallback(type, constraint);
  }

  private recordActiveDevices(): void {
    const activeId = (tracks: MediaStreamTrack[] = []) =>
      tracks.find((track) => track.readyState === 'live')?.getSettings?.().deviceId;

    this.setValues({
      activeCameraId: activeId(this.stream?.getVideoTracks()),
      activeAudioId: activeId(this.stream?.getAudioTracks()),
    });
  }

  public stopStream(): void {
    if (!this.stream) return;
    this.setValues({
      isStreaming: false,
    });
    this.stream.getTracks().forEach((track) => {
      track.stop();
      this.emit('track:stopped', track);
    });
    this.emit('stream:stopped', this);
    this.stream = null;
  }

  private async initDevice(constraints?: MediaStreamConstraints) {
    if (this.deviceThenable) {
      // ensure we get access to devices only once
      return this.deviceThenable;
    }

    const config = this.get() as StreamState;

    constraints = constraints ?? {
      video:
        config.video === undefined
          ? true
          : config.preferredCameraId
            ? { deviceId: { exact: config.preferredCameraId } }
            : config.video === false
              ? false
              : true,

      audio:
        config.audio === undefined
          ? true
          : config.preferredAudioId
            ? { deviceId: { exact: config.preferredAudioId } }
            : config.audio === false
              ? false
              : true,
    };

    this.deviceThenable = this.acquire(constraints).catch((error: unknown) => {
      if (!isDeviceGone(error) || !usesDeviceId(constraints)) {
        throw error;
      }
      return this.acquireIndependently(constraints);
    });
    try {
      const result = await this.deviceThenable;
      return result;
    } finally {
      this.deviceThenable = false;
    }
  }

  private acquire(constraints: MediaStreamConstraints): Promise<MediaStream> {
    const acquisition = this.acquisitions.then(() => navigator.mediaDevices.getUserMedia(constraints));
    this.acquisitions = acquisition.catch(() => undefined);
    return acquisition;
  }

  private acquireWithFallback(
    kind: 'audio' | 'video',
    constraint: NonNullable<MediaStreamConstraints['video']>
  ): Promise<MediaStream> {
    const constraints = singleInputConstraints(kind, constraint);
    const requestedConstraint = constraints[kind];
    return this.acquire(constraints).catch((error: unknown) => {
      if (!hasDeviceIdConstraint(requestedConstraint) || !isDeviceGone(error)) {
        throw error;
      }
      return this.acquire(singleInputConstraints(kind, true));
    });
  }

  private async acquireIndependently(constraints: MediaStreamConstraints): Promise<MediaStream> {
    const tracks: MediaStreamTrack[] = [];
    const releaseTracks = (ownedTracks: MediaStreamTrack[]) => {
      ownedTracks.forEach((track) => {
        if (track.readyState === 'live') track.stop();
        this.pendingAcquisitionTracks.delete(track);
      });
    };

    try {
      for (const kind of ['video', 'audio'] as const) {
        const constraint = constraints[kind];
        if (constraint === undefined || constraint === false) continue;
        if (this.destroyed) {
          throw destroyedAcquisitionError();
        }

        const stream = await this.acquireWithFallback(kind, constraint);
        const acquiredTracks = stream.getTracks();
        if (this.destroyed) {
          releaseTracks(acquiredTracks);
          throw destroyedAcquisitionError();
        }
        tracks.push(...acquiredTracks);
        acquiredTracks.forEach((track) => this.pendingAcquisitionTracks.add(track));
      }
    } catch (error) {
      releaseTracks(tracks);
      throw error;
    }

    const combinedStream = new MediaStream(tracks);
    tracks.forEach((track) => this.pendingAcquisitionTracks.delete(track));
    return combinedStream;
  }

  public async startStream() {
    if (this.destroyed || !this.get('mediaAllowed')) {
      return;
    }
    if (this.stream) return;

    const config = this.get() as StreamState;

    try {
      const stream = await this.initDevice();
      if (this.destroyed) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      this.stream = stream;

      // if we made it to this point, then we know that the user has allowed the browser to access the camera and microphone
      this.setValues({
        isStreaming: true,
        initialized: true,
        streamId: this.stream.id,
        // we can assume the availability of the media devices to true, but we will also check for permissions
        isVideoAllowed: true,
        isAudioAllowed: true,
        ...this.applyMutesTo(this.stream),
      });
      this.recordActiveDevices();

      // we check to make sure mic and camera are enabled. Some older devices will fail here which is why we made the assumption above
      this.checkPermissions();
      void this.refreshAvailableDevices();

      // Update preferences with actual stream capabilities
      const actualConfig = {
        ...config,
        video: this.stream.getVideoTracks().length > 0,
        audio: this.stream.getAudioTracks().length > 0,
      };

      this.savePreferences(actualConfig);
      this.emit('stream:started', this);
      return this.stream;
    } catch (error) {
      if (this.destroyed) return;

      // Video and audio were requested in ONE constraints object, so a camera-only failure
      // would cost the microphone too. Retry once audio-only (`deviceThenable` was cleared in
      // initDevice's `finally`, so the retry cannot dedupe into the failed promise).
      const fallbackAudio = config.preferredAudioId ? { deviceId: { exact: config.preferredAudioId } } : true;
      const fallbackStream = this.canFallBackToAudio(config)
        ? await this.initDevice({ video: false, audio: fallbackAudio }).catch(() => null)
        : null;

      if (this.destroyed) {
        fallbackStream?.getTracks().forEach((track) => track.stop());
        return;
      }

      if (fallbackStream) {
        console.warn(`Camera unavailable, continuing with audio only: ${error}`);
        this.stream = fallbackStream;
        this.setValues({
          isStreaming: true,
          initialized: true,
          streamId: fallbackStream.id,
          isAudioAllowed: true,
          ...this.applyMutesTo(fallbackStream),
        });
        this.recordActiveDevices();

        // Same as the success path: never hard-set `isVideoAllowed` — the Permissions API is
        // the authority, and a busy camera is not a denied camera.
        this.checkPermissions();
        void this.refreshAvailableDevices();

        // Deliberately no `savePreferences`: the fallback must not record video:false as a
        // user choice — the next session/retry still asks for the camera.
        this.emit('stream:started', this);

        // The camera failure is still worth a warning, but only one — the caller must not see
        // both this and a fallback-specific toast.
        showToast(this.toastForMediaError(error));
        return this.stream;
      }

      // A failed grab must NOT be written back as a preference: replaying
      // `{video:false, audio:false}` into getUserMedia is a guaranteed TypeError that would
      // wedge every later attempt. Preferences record user choices, never failures.
      console.error(`Failed to initialize media devices: ${error}`);
      this.emit('stream:error', error);
      showToast(this.toastForMediaError(error));
    }
  }

  /**
   * Carries the user's mutes onto a freshly grabbed stream and returns its enabled flags. A muted
   * kind's new tracks are disabled but stay live, so the mute survives a re-grab (AVS-4440). Any
   * other kind is on exactly when a live track of it exists, so no flag is left `undefined`
   * (read as muted on the wire).
   */
  private applyMutesTo(stream: MediaStream): Pick<StreamState, 'isAudioEnabled' | 'isVideoEnabled'> {
    const flagFor = (kind: 'audio' | 'video'): boolean => {
      const tracks = kind === 'audio' ? stream.getAudioTracks() : stream.getVideoTracks();
      if (this.userMuted[kind]) {
        tracks.forEach((track) => {
          track.enabled = false;
        });
        return false;
      }
      return tracks.some((track) => track.readyState === 'live');
    };
    return { isAudioEnabled: flagFor('audio'), isVideoEnabled: flagFor('video') };
  }

  /**
   * An audio-only retry only makes sense when the failed request asked for the camera (the
   * track we are willing to drop) AND for the microphone (the track we are trying to save).
   * Mirrors the constraint building in `initDevice`.
   */
  private canFallBackToAudio(config: StreamState): boolean {
    const requestedVideo = config.video !== false || Boolean(config.preferredCameraId);
    const requestedAudio = config.audio !== false || Boolean(config.preferredAudioId);
    return requestedVideo && requestedAudio;
  }

  private toastForMediaError(error: unknown): IToastProps {
    return mediaDevicesToastWarnings[(error as Error)?.name] ?? mediaDevicesToastWarnings.default;
  }

  private savePreferences(config: MediaDeviceConfig): void {
    setStorageItem('localStorage', this.storageKey, pickDeviceConfig(config));
  }

  public async getPreferences(): Promise<MediaDeviceConfig> {
    const stored: MediaDeviceConfig | null = getStorageItem('localStorage', this.storageKey);
    // Older builds stored the full state; a restored mute flag would lie to peers (AVS-4440).
    const preferences = stored ? pickDeviceConfig(stored) : null;

    // `{video:false, audio:false}` can only be the residue of a failed grab (requesting
    // neither track is an instant TypeError) — treat it as absent so poisoned machines self-heal.
    const isPoisoned = preferences?.video === false && preferences?.audio === false;

    if (preferences && !isPoisoned) {
      return preferences;
    }

    if (!this.get('videoDevices') || !this.get('audioDevices')) {
      await this.getAvailableDevices();
    }

    const defaultPreferences = {
      video: true,
      audio: true,
    };

    return defaultPreferences;
  }

  public getDefaultVideoDeviceId(): string {
    return this.get('videoDevices')?.[0]?.id || '';
  }

  public getDefaultAudioDeviceId(): string {
    return this.get('audioDevices')?.[0]?.id || '';
  }

  public getSelectedDeviceId(kind: 'video' | 'audio'): string | null {
    const state = this.get() as StreamState;
    const activeId = kind === 'video' ? state.activeCameraId : state.activeAudioId;
    const preferredId = kind === 'video' ? state.preferredCameraId : state.preferredAudioId;
    const devices = kind === 'video' ? state.videoDevices : state.audioDevices;
    return activeId ?? preferredId ?? devices?.[0]?.id ?? null;
  }

  public checkPermissions(): void {
    try {
      navigator.permissions.query({ name: 'camera' as PermissionName }).then((result) => {
        this.permissions = {
          video: result.state === 'granted',
          audio: this.permissions?.audio || false,
        };

        this.setValues({ isVideoAllowed: result.state === 'granted' });
      });

      navigator.permissions.query({ name: 'microphone' as PermissionName }).then((result) => {
        this.permissions = {
          audio: result.state === 'granted',
          video: this.permissions?.video || false,
        };

        this.setValues({ isAudioAllowed: result.state === 'granted' });
      });
    } catch {
      // It looks like the browser doesn't support permissions query
    }
  }

  /**
   * Switch the active device for a given kind (camera or microphone).
   * Re-acquires the track with the new deviceId constraint, removes the old track,
   * and saves the preference to localStorage.
   *
   * @param kind - 'video' for camera, 'audio' for microphone
   * @param deviceId - The device ID to switch to
   * @throws Error if there is no active stream
   */
  public switchDevice(kind: 'video' | 'audio', deviceId: string): Promise<void> {
    const previousOperation = this.reacquisitions[kind] ?? Promise.resolve();
    const operation = previousOperation
      .catch(() => undefined)
      .then(async () => {
        const streamAtStart = this.stream;
        if (!streamAtStart) {
          throw new Error('No active stream to switch devices');
        }

        const constraints: MediaStreamConstraints = {
          video: kind === 'video' ? { deviceId: { exact: deviceId } } : false,
          audio: kind === 'audio' ? { deviceId: { exact: deviceId } } : false,
        };
        let newStream: MediaStream;
        try {
          newStream = await this.acquire(constraints);
        } catch (error) {
          showToast(this.toastForMediaError(error));
          throw error;
        }

        if (this.stream !== streamAtStart) {
          newStream.getTracks().forEach((track) => track.stop());
          return;
        }

        const newTracks = (kind === 'audio' ? newStream.getAudioTracks() : newStream.getVideoTracks()).filter(
          (track) => track.readyState === 'live'
        );
        if (newTracks.length === 0) {
          newStream.getTracks().forEach((track) => track.stop());
          const error = new Error(`getUserMedia returned no ${kind} track`);
          error.name = 'NotFoundError';
          showToast(this.toastForMediaError(error));
          throw error;
        }

        const oldTracks = kind === 'audio' ? streamAtStart.getAudioTracks() : streamAtStart.getVideoTracks();
        for (const track of oldTracks) {
          if (track.readyState === 'live') {
            track.stop();
          }
          streamAtStart.removeTrack(track);
          this.emit('track:removed', track);
          this.emit('track:stopped', track);
        }
        if (this.stream !== streamAtStart) {
          newStream.getTracks().forEach((track) => track.stop());
          return;
        }

        const enabled = !this.userMuted[kind];
        if (enabled) {
          for (const track of newTracks) {
            streamAtStart.addTrack(track);
          }
          this.setValues(kind === 'video' ? { isVideoEnabled: true } : { isAudioEnabled: true });
          newTracks.forEach((track) => this.emit('track:added', track, kind));
        } else {
          newTracks.forEach((track) => track.stop());
        }
        this.recordActiveDevices();

        this.setValues({
          [kind === 'video' ? 'preferredCameraId' : 'preferredAudioId']: deviceId,
        });
      });

    this.reacquisitions[kind] = operation;
    const clearIfCurrent = () => {
      if (this.reacquisitions[kind] === operation) {
        delete this.reacquisitions[kind];
      }
    };
    void operation.then(clearIfCurrent, clearIfCurrent);
    return operation;
  }

  destroy() {
    this.destroyed = true;
    navigator.mediaDevices?.removeEventListener?.('devicechange', this.handleDeviceChange);
    this.pendingAcquisitionTracks.forEach((track) => {
      if (track.readyState === 'live') track.stop();
    });
    this.pendingAcquisitionTracks.clear();

    this.stream?.getTracks().forEach((track) => {
      track.stop();
      this.stream?.removeTrack(track);
    });

    this.stopStream();
    this.emit('destroyed');
    setTimeout(() => super.destroy(), 0);
  }
}
